import React from 'react';
import { Button, Empty, Modal, Typography } from 'antd';
import { RichTextViewer } from '../../components/RichTextEditor';
import { authorName } from './authors';
import { formatLogged } from './entryTable';
import IssueListHeadMeta from './IssueListHeadMeta';
import {
	useGetProgramIssuesQuery,
	useGetProgramQuery,
} from '../../redux/api/manufacturing';

const { Text } = Typography;

interface ProgramIssuesModalProps {
	programId: string | null;
	partName: string;
	open: boolean;
	onClose: () => void;
}

/**
 * All issues raised against one program, read-only.
 *
 * The scoring grid keeps issues as a count until the user asks to see them.
 */
const ProgramIssuesModal: React.FC<ProgramIssuesModalProps> = ({
	programId,
	partName,
	open,
	onClose,
}) => {
	const { data, isLoading } = useGetProgramIssuesQuery(
		{ program: programId as string },
		{ skip: !programId || !open }
	);
	const { data: program } = useGetProgramQuery(programId as string, {
		skip: !programId || !open,
	});
	const issues = data?.results || [];

	return (
		<Modal
			className="cam-modal"
			title={`Issues — ${partName}`}
			open={open}
			centered
			onCancel={onClose}
			footer={<Button onClick={onClose}>Close</Button>}
			width={780}
			destroyOnClose
		>
			{isLoading ? (
				<Text type="secondary">Loading issues…</Text>
			) : issues.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="No issues reported"
				/>
			) : (
				<div className="cam-thread__issues">
					{issues.map((issue) => (
						<div
							key={issue.id}
							className={`cam-issue cam-issue--${issue.severity}`}
						>
							<div className="cam-issue__head">
								<span className="cam-issue__severity">
									{issue.severity_display}
								</span>
								<IssueListHeadMeta issue={issue} program={program} />
								<Text type="secondary" className="cam-issue__by" ellipsis>
									Reported by {authorName(issue.reported_by, 'Unknown')}
								</Text>
								<Text type="secondary" className="cam-thread__time">
									{formatLogged(issue.created_time)}
								</Text>
							</div>
							<RichTextViewer
								html={issue.description}
								emptyText="No description"
								className="cam-entry__body"
							/>
						</div>
					))}
				</div>
			)}
		</Modal>
	);
};

export default ProgramIssuesModal;
